import { useEffect, useState } from "react";
import { ChatCircleDots, CaretDown, CaretUp, HandPalm, EnvelopeSimple, CursorClick } from "@phosphor-icons/react";
import client from "../lib/api";

const ago = (iso) => {
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso)) / 60000));
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
  return `${Math.round(mins / 1440)}d ago`;
};

export const ChatConversationsCard = () => {
  const [data, setData] = useState(null);
  const [openSid, setOpenSid] = useState(null);
  const [transcripts, setTranscripts] = useState({});

  useEffect(() => {
    client.get("/admin/chat-conversations").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const toggle = async (sid) => {
    if (openSid === sid) return setOpenSid(null);
    setOpenSid(sid);
    if (!transcripts[sid]) {
      try {
        const r = await client.get(`/public/lead-chat/${sid}/history`);
        setTranscripts((p) => ({ ...p, [sid]: r.data.messages || [] }));
      } catch { /* leave empty */ }
    }
  };

  if (!data || (!data.sessions?.length && !data.quick_taps?.length)) return null;

  return (
    <div className="mt-6 card-surface p-4" data-testid="chat-conversations-card">
      <div className="flex items-center gap-2 mb-3">
        <ChatCircleDots size={14} weight="bold" color="#FF9764" />
        <span className="overline">AI CHAT CONVERSATIONS · LANDING PAGES</span>
        <span className="ml-auto text-[10px] font-mono text-neutral-400">
          {data.sessions.length} recent
        </span>
      </div>

      {data.quick_taps?.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-1.5" data-testid="chat-quick-tap-stats">
          <CursorClick size={12} weight="bold" color="#888" />
          <span className="text-[10px] text-neutral-500 mr-1">Starter taps:</span>
          {data.quick_taps.map((t) => (
            <span key={t.question}
                  data-testid={`chat-quick-tap-${t.question.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+$/, "")}`}
                  className="rounded-full bg-white/[0.05] border border-white/10 px-2.5 py-1 text-[10px] text-neutral-300">
              {t.question} <b className="text-[#FF9764]">×{t.count}</b>
            </span>
          ))}
        </div>
      )}

      <div className="space-y-1.5">
        {data.sessions.map((s) => (
          <div key={s.session_id} className="rounded-lg bg-white/[0.03]">
            <button
              type="button"
              onClick={() => toggle(s.session_id)}
              data-testid={`chat-session-${s.session_id}`}
              className="w-full flex items-center gap-3 px-3 py-2 text-left"
            >
              <span className="shrink-0 text-[10px] font-mono text-neutral-500 w-14">{ago(s.last_at)}</span>
              <span className={`shrink-0 inline-flex items-center gap-1 text-[11px] font-bold ${s.email ? "text-emerald-400" : "text-neutral-400"}`}>
                {s.email ? <><EnvelopeSimple size={11} weight="fill" />{s.email}</> : "anonymous"}
              </span>
              {s.handoff && (
                <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-[#FF5A1F]/15 border border-[#FF5A1F]/40 px-2 py-0.5 text-[9px] font-bold text-[#FF9764]"
                      data-testid={`chat-handoff-badge-${s.session_id}`}>
                  <HandPalm size={10} weight="fill" /> NEEDS YOU
                </span>
              )}
              <span className="min-w-0 flex-1 truncate text-[11px] text-neutral-400">"{s.preview}"</span>
              <span className="shrink-0 text-[10px] font-mono text-neutral-500">{s.messages} msg{s.messages === 1 ? "" : "s"}</span>
              {openSid === s.session_id ? <CaretUp size={12} color="#888" /> : <CaretDown size={12} color="#888" />}
            </button>
            {openSid === s.session_id && (
              <div className="border-t border-white/5 px-3 py-2.5 space-y-2" data-testid={`chat-transcript-${s.session_id}`}>
                {(transcripts[s.session_id] || []).map((m, i) => (
                  <div key={i}
                       className={`max-w-[80%] rounded-xl px-3 py-2 text-[12px] leading-relaxed whitespace-pre-wrap ${
                         m.role === "user"
                           ? "ml-auto bg-[#FF5A1F]/15 border border-[#FF5A1F]/30 text-white"
                           : "bg-white/[0.05] text-neutral-300"
                       }`}>
                    {m.content}
                  </div>
                ))}
                {!(transcripts[s.session_id] || []).length && (
                  <div className="text-[11px] text-neutral-500">Loading transcript…</div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
